import { Suspense } from "react";

import {
  ChevronDownIcon,
  Button,
  SingleSelectDropdown,
  SingleSelectDropdownItem,
} from "@stacklet/ui";

import { TableSkeleton } from "app/components/skeletons";
import { useSearchParamState } from "app/hooks";

import { GroupUsersGrouping } from "./GroupUsersGrouping";
import { IdpUsersGrouping } from "./IdpUsersGrouping";
import { RoleUsersGrouping } from "./RoleUsersGrouping";
import { UserBrowser } from "./UserBrowser";

/** How the directory is grouped; "none" is the flat user list. */
type GroupBy = "none" | "group" | "role" | "identity-provider";

/** Group-by options, in display order. */
const GROUP_BY_OPTIONS: { id: GroupBy; label: string }[] = [
  { id: "none", label: "None" },
  { id: "group", label: "Group" },
  { id: "role", label: "Role" },
  { id: "identity-provider", label: "Identity Provider" },
];

/** The querystring key the grouping persists under. */
const GROUP_BY_PARAM = "group-by";

/** The groupings a `?group-by=` value may name; anything else reads as "none". */
const GROUP_BY_IDS = GROUP_BY_OPTIONS.map((option) => option.id);

interface GroupByDropdownProps {
  /** The currently selected grouping. */
  value: GroupBy;
  /** Called when a grouping is chosen. */
  onChange: (value: GroupBy) => void;
}

/** The toolbar dropdown that chooses the grouping. */
function GroupByDropdown({ value, onChange }: GroupByDropdownProps) {
  const active =
    GROUP_BY_OPTIONS.find((option) => option.id === value) ??
    GROUP_BY_OPTIONS[0];

  return (
    <SingleSelectDropdown
      AnchorEle={
        <Button
          IconRight={<ChevronDownIcon size={12} />}
          aria-label="Group by"
          className="whitespace-nowrap py-xxs"
          variant="tertiary"
        >
          <span className="text-text-secondary">Group by:</span> {active.label}
        </Button>
      }
      placement="bottom right"
    >
      {GROUP_BY_OPTIONS.map((option) => (
        <SingleSelectDropdownItem
          className="whitespace-nowrap"
          isSelected={option.id === value}
          key={option.id}
          onPress={() => onChange(option.id)}
        >
          {option.label}
        </SingleSelectDropdownItem>
      ))}
    </SingleSelectDropdown>
  );
}

/**
 * The settings users directory. Ungrouped it's a flat {@link UserBrowser} over
 * every user; grouped (by group/role/identity provider) it's a
 * {@link GroupedUsersGrid} of expandable sections. The group-by dropdown lives
 * in the grid toolbar.
 */
export function UsersDirectory() {
  // Both the grouping and the search term live in the URL, so the view
  // survives a reload and can be linked to. Keeping the term here also means it
  // survives switching the grouping, which swaps one child grid for another.
  const [groupBy, setGroupBy] = useSearchParamState<GroupBy>(GROUP_BY_PARAM, {
    allowed: GROUP_BY_IDS,
    defaultValue: "none",
  });
  const [searchTerm, setSearchTerm] = useSearchParamState("q", {
    defaultValue: "",
  });

  const toolbarActions = (
    <GroupByDropdown onChange={setGroupBy} value={groupBy} />
  );

  return (
    <Suspense fallback={<TableSkeleton size="medium" />}>
      {groupBy === "none" ? (
        <UserBrowser
          defaultPageSize={25}
          initialSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          scope={{ kind: "all" }}
          tableId="users-directory-all"
          title={null}
          toolbarActions={toolbarActions}
          exportTrailing
          transparentSearch
        />
      ) : groupBy === "group" ? (
        <GroupUsersGrouping
          initialSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          toolbarActions={toolbarActions}
        />
      ) : groupBy === "role" ? (
        <RoleUsersGrouping
          initialSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          toolbarActions={toolbarActions}
        />
      ) : (
        <IdpUsersGrouping
          initialSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          toolbarActions={toolbarActions}
        />
      )}
    </Suspense>
  );
}
